import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, TrendingUp, Trophy, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { GS } from '../lib/designTokens';
import { track } from '../lib/analytics';

interface Props {
  rankedPlayers: any[];
  onStartTrial: () => void;
  onSkip: () => void;
}

// One-screen Elite pitch shown right after the coach scouts their roster. Rather
// than a checklist (which read as AI-designed), each Elite feature is a small
// LIVE-LOOKING product mockup — the same craft as the landing page's Ryan Davis
// card — that the coach swipes through. Light/slate theme to match
// dugoutbaseball.app; the "Start Free Trial" CTA stays pinned below the carousel.
// Voice: development-is-the-ceiling, no em-dashes (brand copy rule).

// ── Shared mockup chrome ──────────────────────────────────────────────────────

const MockCard: React.FC<{ live?: string; tag?: string; children: React.ReactNode }> = ({ live, tag, children }) => (
  <div style={{ background: GS.panel, border: `1px solid ${GS.line}`, borderRadius: 20, overflow: 'hidden', boxShadow: '0 20px 50px -24px rgba(20,26,33,0.28)' }}>
    {(live || tag) && (
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${GS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {live && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase', color: GS.greenInk }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: GS.green, boxShadow: `0 0 8px ${GS.green}` }} />{live}
          </span>
        )}
        {tag && <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: GS.ink3 }}>{tag}</span>}
      </div>
    )}
    {children}
  </div>
);

// ── The five feature mockups ──────────────────────────────────────────────────

const XovrMock = () => (
  <MockCard live="Scouting → xOVR">
    <div style={{ padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 46, height: 46, borderRadius: 13, background: GS.panel2, border: `1px solid ${GS.line}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900 }}>8</div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', color: GS.ink }}>Ryan Davis</p>
          <p style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: GS.ink3, marginTop: 2 }}>SS · your #3 hitter</p>
        </div>
      </div>
      <div style={{ width: 54, height: 54, borderRadius: 15, background: `linear-gradient(150deg, ${GS.green}, ${GS.green2})`, color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: `0 8px 24px ${GS.greenGlow}` }}>
        <span style={{ fontSize: 21, fontWeight: 900, fontStyle: 'italic', lineHeight: 1 }}>84</span>
        <span style={{ fontSize: 7, fontWeight: 900, letterSpacing: '0.14em' }}>xOVR</span>
      </div>
    </div>
    <div style={{ margin: '0 16px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 800, color: GS.ink3, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Your eye 78</span>
      <div style={{ flex: 1, height: 6, background: GS.panel2, borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        <div style={{ position: 'absolute', inset: 0, width: '84%', background: `linear-gradient(90deg, ${GS.green}, ${GS.green2})`, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 10, fontWeight: 900, color: GS.greenInk, textTransform: 'uppercase', letterSpacing: '0.08em' }}>+6 stats</span>
    </div>
  </MockCard>
);

const ScoutMock = () => (
  <MockCard tag="Smart Scout · after the game">
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: 9, background: GS.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Sparkles style={{ width: 15, height: 15, color: GS.green }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', color: GS.ink }}>Coach's Eye</span>
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.5, color: GS.ink, fontWeight: 500 }}>
        Marcus is squaring up everything but hitting into outs. Move him up to 2-hole and let him drive the top of your order.
      </p>
      <div style={{ marginTop: 12, background: GS.greenSoft, border: `1px solid ${GS.green}`, borderRadius: 12, padding: '9px 12px', fontSize: 11, fontWeight: 700, color: GS.greenInk }}>
        Suggested: bat Marcus 2nd next game
      </div>
    </div>
  </MockCard>
);

const StatsMock = () => (
  <MockCard tag="The story behind the box score">
    <div style={{ padding: 16, display: 'grid', gap: 8 }}>
      {[
        { n: '12', l: "Mason: hot streak", s: '6-game hit streak', hot: true },
        { n: '3', l: 'Ethan: bailed out', s: '3 BB, but good defense saved 4 runs', hot: false },
      ].map((r) => (
        <div key={r.l} style={{ display: 'flex', alignItems: 'center', gap: 12, background: GS.panel2, border: `1px solid ${GS.line}`, borderRadius: 13, padding: '10px 12px' }}>
          <div style={{ width: 38, height: 38, borderRadius: 11, background: r.hot ? `linear-gradient(150deg, ${GS.green}, ${GS.green2})` : GS.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 900, fontStyle: 'italic' }}>{r.n}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, fontWeight: 900, textTransform: 'uppercase', color: GS.ink }}>{r.l}</p>
            <p style={{ fontSize: 10, color: GS.ink2, marginTop: 1 }}>{r.s}</p>
          </div>
          <span style={{ fontSize: 16 }}>{r.hot ? '🔥' : '🧤'}</span>
        </div>
      ))}
    </div>
  </MockCard>
);

const TournamentMock = () => (
  <MockCard live="Pool play · 2 of 4">
    <div style={{ padding: 16 }}>
      <p style={{ fontSize: 12, fontWeight: 900, textTransform: 'uppercase', color: GS.ink, marginBottom: 10 }}>Bullpen Status: All Star Tournament</p>
      {[
        { name: 'Davis', used: 47, max: 75, ok: 'eligible Sun' },
        { name: 'Chen', used: 68, max: 75, ok: 'resting' },
        { name: 'Brooks', used: 12, max: 75, ok: 'ready' },
      ].map((p) => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: GS.ink, width: 48 }}>{p.name}</span>
          <div style={{ flex: 1, height: 7, background: GS.panel2, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${(p.used / p.max) * 100}%`, background: p.used > 60 ? GS.gold : GS.green, borderRadius: 4 }} />
          </div>
          <span style={{ fontSize: 9, fontWeight: 800, color: p.used > 60 ? GS.goldInk : GS.greenInk, width: 62, textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{p.ok}</span>
        </div>
      ))}
    </div>
  </MockCard>
);

const AssistantMock = () => (
  <MockCard tag="Your whole staff, one rating">
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 900, textTransform: 'uppercase', color: GS.ink }}>Jordan · Glove</span>
        <span style={{ fontSize: 20, fontWeight: 900, fontStyle: 'italic', color: GS.greenInk }}>4.3</span>
      </div>
      {[
        { who: 'You', rate: 4, tint: GS.ink },
        { who: 'Coach Mike', rate: 5, tint: GS.green },
        { who: 'Coach Bryce', rate: 4, tint: GS.green },
      ].map((c) => (
        <div key={c.who} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: GS.ink2, width: 78 }}>{c.who}</span>
          <div style={{ display: 'flex', gap: 3 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} style={{ width: 16, height: 16, borderRadius: 5, background: n <= c.rate ? c.tint : GS.panel2, border: `1px solid ${n <= c.rate ? c.tint : GS.line}` }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </MockCard>
);

interface Feature {
  key: string;
  icon: React.ComponentType<{ style?: React.CSSProperties }>;
  label: string;
  blurb: string;
  Mock: React.FC;
}

const FEATURES: Feature[] = [
  { key: 'xovr', icon: TrendingUp, label: 'xOVR', blurb: 'Your coaching eye, proven by real game stats. Tracked all season so you see who is developing.', Mock: XovrMock },
  { key: 'tourney', icon: Trophy, label: 'Tournament mode', blurb: 'Guest players, bracket tracking, and bullpen counts across the weekend.', Mock: TournamentMock },
  { key: 'scout', icon: Sparkles, label: 'Smart Scout AI', blurb: 'Player insights after every game so you see everything.', Mock: ScoutMock },
  { key: 'stats', icon: TrendingUp, label: 'Stats that tell stories', blurb: "Who's hot (or not), who's lucky (or not).", Mock: StatsMock },
  { key: 'assist', icon: Users, label: 'Assistant-coach scouting', blurb: 'Get Assistant Coach evaluations, and factor them into xOVR.', Mock: AssistantMock },
];

export const ScoutingElitePrompt: React.FC<Props> = ({ onStartTrial, onSkip }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  // Top of the trial funnel. Everything downstream is measured as a fraction of
  // this, so it fires on mount, once, whether or not the coach engages.
  useEffect(() => { track('elite_prompt_shown'); }, []);

  // Cards are 88% of the viewport, so index math uses the actual card width
  // (the first child's offsetWidth) rather than the scroller's client width.
  const cardWidth = () => (scrollerRef.current?.firstElementChild as HTMLElement | null)?.offsetWidth || scrollerRef.current?.clientWidth || 1;

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const i = Math.round(el.scrollLeft / cardWidth());
    if (i !== index) setIndex(i);
  };

  const goTo = (i: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(FEATURES.length - 1, i));
    el.scrollTo({ left: clamped * cardWidth(), behavior: 'smooth' });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{
        background: GS.bg,
        paddingTop: 'calc(env(safe-area-inset-top) + 18px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
      }}
    >
      {/* Header — logo mark + the hook */}
      <div className="shrink-0 px-6 text-center">
        <div className="mx-auto mb-3" style={{ width: 52, height: 52, borderRadius: 15, overflow: 'hidden', boxShadow: `0 8px 30px ${GS.greenGlow}` }}>
          <img src="/dugout-mark.png" alt="Dugout" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', letterSpacing: '-0.01em', color: GS.ink }}>
          You just built xOVR.
        </h2>
        <p style={{ fontSize: 13, fontWeight: 600, color: GS.ink2, lineHeight: 1.4, marginTop: 6, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
          Here's what your coaching eye plus real game stats unlocks with Elite.
        </p>
      </div>

      {/* Carousel — swipe through live-looking feature mockups. Each slide is
          slightly narrower than the viewport so the NEXT card peeks in from the
          right edge (the universal "there's more, swipe" cue) — so the coach
          never thinks xOVR is the only feature. Arrow buttons back it up on web. */}
      <div className="flex-1 min-h-0 flex flex-col justify-center relative">
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="flex overflow-x-auto snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
        >
          {FEATURES.map(({ key, icon: Icon, label, blurb, Mock }) => (
            // 88% width leaves ~12% for the neighbor to peek; pl on the first and
            // pr on the last keep the active card centered at the ends.
            <div
              key={key}
              className="snap-center shrink-0"
              style={{ width: '88%', paddingLeft: '2.5%', paddingRight: '2.5%' }}
            >
              <div style={{ maxWidth: 360, margin: '0 auto' }}>
                {/* Feature label */}
                <div className="flex items-center gap-2.5 mb-3">
                  <div style={{ width: 34, height: 34, borderRadius: 10, background: GS.greenSoft, border: `1px solid ${GS.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon style={{ width: 17, height: 17, color: GS.greenInk }} />
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', color: GS.ink }}>{label}</span>
                </div>
                {/* Live-looking product mockup */}
                <Mock />
                {/* One-line benefit */}
                <p style={{ fontSize: 12.5, color: GS.ink2, lineHeight: 1.5, marginTop: 12, textAlign: 'center', paddingLeft: 8, paddingRight: 8 }}>{blurb}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Prev / next arrows — obvious carousel affordance (esp. on web, where
            there's no swipe instinct). Each hides at its end of the track. */}
        {index > 0 && (
          <button
            onClick={() => goTo(index - 1)}
            aria-label="Previous feature"
            className="absolute left-1 top-1/2 -translate-y-1/2 flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: '50%', background: GS.panel, border: `1px solid ${GS.line}`, boxShadow: '0 4px 14px -4px rgba(20,26,33,0.25)' }}
          >
            <ChevronLeft style={{ width: 20, height: 20, color: GS.ink }} />
          </button>
        )}
        {index < FEATURES.length - 1 && (
          <button
            onClick={() => goTo(index + 1)}
            aria-label="Next feature"
            className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: '50%', background: GS.panel, border: `1px solid ${GS.line}`, boxShadow: '0 4px 14px -4px rgba(20,26,33,0.25)' }}
          >
            <ChevronRight style={{ width: 20, height: 20, color: GS.ink }} />
          </button>
        )}

        {/* Dot indicators */}
        <div className="flex items-center justify-center gap-2 mt-5">
          {FEATURES.map((f, i) => (
            <button
              key={f.key}
              onClick={() => goTo(i)}
              aria-label={`Go to ${f.label}`}
              style={{
                height: 7,
                width: i === index ? 20 : 7,
                borderRadius: 4,
                background: i === index ? GS.green : GS.line,
                transition: 'all 0.25s',
              }}
            />
          ))}
        </div>
      </div>

      {/* Pinned CTA */}
      <div className="shrink-0 px-6 mt-4" style={{ maxWidth: 400, marginLeft: 'auto', marginRight: 'auto', width: '100%' }}>
        <button
          onClick={() => { track('start_trial_tapped'); onStartTrial(); }}
          style={{ width: '100%', padding: '16px 0', background: GS.ink, color: '#fff', fontWeight: 900, fontStyle: 'italic', textTransform: 'uppercase', fontSize: 17, borderRadius: 16, boxShadow: `0 10px 30px ${GS.greenGlow}` }}
        >
          Start Free 14-Day Trial →
        </button>
        <p style={{ fontSize: 10, color: GS.ink3, fontWeight: 500, textAlign: 'center', marginTop: 8 }}>
          14 days free. No charge today. Cancel anytime in Settings.
        </p>
        <button
          onClick={onSkip}
          style={{ width: '100%', fontSize: 11, fontWeight: 700, color: GS.ink3, textTransform: 'uppercase', letterSpacing: '0.12em', padding: '6px 0', marginTop: 2 }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};
